import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Search, ShieldCheck, AlertTriangle, ChevronLeft, Database } from 'lucide-react';
import './DnaCommandCenter.css';

const fmtDate = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('pt-BR'); } catch { return String(d); } };

export function DataAuditStudent() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) return;
    api.get(`/dnacore/v4/audit/student/${id}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Erro'));
  }, [id]);

  return (
    <div className="dnav3-home" style={{ minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 2rem 3rem' }}>
        <button onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#a5b4fc', cursor: 'pointer', marginBottom: 16 }}>
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#8a93a6' }}>DNACore V4 · Data Integrity</div>
        <h1 className="hero" style={{ fontSize: '2.4rem', marginTop: 6 }}>
          <Search className="w-7 h-7 inline" style={{ color: '#22d3ee', marginRight: 8 }} />
          Auditoria · {data?.student_name || '...'}
        </h1>
        <div style={{ color: '#8a93a6', fontSize: 12, marginTop: 4 }}>
          Gerado em {fmtDate(data?.generated_at)} · Origem total: <a href="/api/dnacore/v4/origin" target="_blank" rel="noreferrer" style={{ color: '#a5b4fc' }}>manifesto</a>
        </div>

        {error && <div style={{ marginTop: 24, color: '#ef4444' }}>{error}</div>}
        {!data && !error && <div className="dnav3-skel-row" style={{ height: 200, marginTop: 24 }} />}

        {data && data.sections.map((sec, idx) => (
          <div key={idx} className="dnav3-card-v3" style={{ marginTop: 16 }} data-testid={`v4-section-${idx}`}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <Database className="w-4 h-4" style={{ color: '#22d3ee' }} />
              <div style={{ fontWeight: 800, fontSize: 16 }}>{sec.title}</div>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: '#8a93a6' }}>{sec.count != null ? `${sec.count} registros` : ''}</span>
              <span style={{ fontSize: 10, padding: '3px 8px', borderRadius: 999, background: 'rgba(34,211,238,0.10)', color: '#67e8f9', border: '1px solid rgba(34,211,238,0.30)', fontFamily: 'ui-monospace,monospace' }}>{sec.source}</span>
            </div>
            {(sec.rows || []).length === 0 ? (
              <div style={{ color: '#8a93a6', fontSize: 12 }}>Sem registros nesta fonte.</div>
            ) : (
              <table style={{ width: '100%', fontSize: 12, color: '#cbd5e1' }}>
                <thead>
                  <tr style={{ color: '#8a93a6', fontSize: 10, letterSpacing: 1, textTransform: 'uppercase' }}>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Chave</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Valor</th>
                    <th style={{ textAlign: 'left', padding: '6px 4px' }}>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {sec.rows.map((row, i) => (
                    <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '6px 4px' }}>{String(row.key ?? '—')}</td>
                      <td style={{ padding: '6px 4px', fontFamily: 'ui-monospace,monospace' }}>
                        {String(row.value ?? '—')}
                        {row.status && <span style={{ marginLeft: 6, fontSize: 10, color: '#8a93a6' }}>({row.status})</span>}
                        {row.recuperacao && <span style={{ marginLeft: 6, fontSize: 10, color: '#fbbf24' }}>· recuperação</span>}
                      </td>
                      <td style={{ padding: '6px 4px', color: '#8a93a6' }}>{fmtDate(row.ts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export function DataHealthDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [origin, setOrigin] = useState(null);

  useEffect(() => {
    api.get('/dnacore/v4/health').then((r) => setData(r.data)).catch(() => { /* noop */ });
    api.get('/dnacore/v4/origin').then((r) => setOrigin(r.data)).catch(() => { /* noop */ });
  }, []);

  const color = (s) => s === 'ok' ? '#22c55e' : s === 'warn' ? '#f59e0b' : s === 'bad' ? '#ef4444' : '#8a93a6';
  const icon = (s) => s === 'ok' ? '🟢' : s === 'warn' ? '🟡' : '🔴';

  return (
    <div className="dnav3-home" style={{ minHeight: '100vh' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '2.5rem 2rem 3rem' }}>
        <button onClick={() => navigate(-1)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent', border: 'none', color: '#a5b4fc', cursor: 'pointer', marginBottom: 16 }}>
          <ChevronLeft className="w-4 h-4" /> Voltar
        </button>
        <div style={{ fontSize: 11, letterSpacing: 3, textTransform: 'uppercase', color: '#8a93a6' }}>DNACore V4 · Data Integrity</div>
        <h1 className="hero" style={{ fontSize: '2.4rem', marginTop: 6 }}>
          <ShieldCheck className="w-7 h-7 inline" style={{ color: '#22c55e', marginRight: 8 }} />
          Health Check Institucional
        </h1>
        <div style={{ color: '#8a93a6', fontSize: 12, marginTop: 4 }}>
          Overall: <span style={{ color: color(data?.overall), fontWeight: 800 }}>{icon(data?.overall)} {data?.overall || '...'}</span> · {fmtDate(data?.generated_at)}
        </div>

        <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {!data && <div className="dnav3-skel-row" style={{ height: 120 }} />}
          {data?.areas?.map((a) => (
            <div key={a.area} className="dnav3-card-v3" data-testid={`v4-health-${a.area}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 18 }}>{icon(a.status)}</span>
                <div style={{ fontWeight: 800, fontSize: 16, textTransform: 'capitalize' }}>{a.area.replace('_', ' ')}</div>
                <span style={{ marginLeft: 'auto', fontSize: 10, padding: '3px 8px', borderRadius: 999, background: 'rgba(34,211,238,0.10)', color: '#67e8f9', border: '1px solid rgba(34,211,238,0.30)', fontFamily: 'ui-monospace,monospace' }}>{a.source}</span>
              </div>
              <div style={{ marginTop: 8, fontSize: 22, fontWeight: 900, color: color(a.status) }}>{a.total != null ? a.total : '—'} registros</div>
              {(a.issues || []).map((i, idx) => (
                <div key={idx} style={{ marginTop: 6, fontSize: 12, color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle className="w-3.5 h-3.5" style={{ color: color(a.status) }} /> {i}
                </div>
              ))}
              {a.timezone && <div style={{ marginTop: 6, fontSize: 11, color: '#8a93a6' }}>TZ: {a.timezone}</div>}
            </div>
          ))}
        </div>

        {/* Manifesto de origem */}
        {origin && (
          <div className="dnav3-card-v3" style={{ marginTop: 28 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Database className="w-4 h-4" style={{ color: '#22d3ee' }} />
              <div style={{ fontWeight: 800, fontSize: 16 }}>Manifesto de Origem · Single Source of Truth</div>
            </div>
            <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 8 }}>
              {Object.entries(origin.manifest).map(([key, val]) => (
                <div key={key} style={{ padding: 10, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12 }}>
                  <div style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11, color: '#67e8f9' }}>{val.collection}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</div>
                  <div style={{ marginTop: 6, fontSize: 10, color: '#8a93a6' }}>{val.consumed_by.join(' · ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
